import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Search, CheckCircle2, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { COVERAGE_ZONES, IMAGES } from "@/data/content";

export function CoverageMap() {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(null);

  const filtered = COVERAGE_ZONES.filter(
    (z) =>
      z.city.toLowerCase().includes(query.toLowerCase()) ||
      (z.neighborhoods && z.neighborhoods.some((n) => n.toLowerCase().includes(query.toLowerCase())))
  );

  return (
    <div className="grid lg:grid-cols-2 gap-8 items-start" data-testid="coverage-map">
      <div>
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Busca tu barrio en El Carmen de Viboral..."
            className="pl-12 h-13 rounded-full text-base"
            data-testid="coverage-search"
          />
        </div>
        <div className="space-y-3 max-h-[420px] overflow-y-auto pr-2">
          {filtered.length === 0 && (
            <p className="text-muted-foreground text-sm py-8 text-center">
              No encontramos cobertura para "{query}". Escríbenos por WhatsApp y validamos tu dirección.
            </p>
          )}
          {filtered.map((z) => (
            <button
              key={z.city}
              onMouseEnter={() => setActive(z.city)}
              onFocus={() => setActive(z.city)}
              data-testid={`coverage-zone-${z.city.toLowerCase().replace(/\s/g, "-")}`}
              className={`w-full text-left rounded-2xl border p-4 transition-all hover:-translate-y-0.5 hover:shadow-lg ${active === z.city ? "border-primary bg-accent" : "border-border bg-card"
                }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-display font-bold text-lg">{z.city}</span>
                {z.status === "Disponible" ? (
                  <Badge className="gap-1 bg-emerald-500 hover:bg-emerald-500"><CheckCircle2 className="h-3 w-3" /> {z.status}</Badge>
                ) : (
                  <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" /> {z.status}</Badge>
                )}
              </div>
              {z.neighborhoods && z.neighborhoods.length > 0 && (
                <p className="text-sm text-muted-foreground">{z.neighborhoods.join(" · ")}</p>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="relative rounded-3xl overflow-hidden border border-border aspect-square lg:aspect-auto lg:h-[490px] bg-secondary">
        <img src={IMAGES.bogota} alt="Cobertura Colnexx" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-secondary/40" />
        {COVERAGE_ZONES.map((z) => (
          <motion.button
            key={z.city}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            onMouseEnter={() => setActive(z.city)}
            style={{ left: `${z.coords.x}%`, top: `${z.coords.y}%` }}
            className="absolute -translate-x-1/2 -translate-y-1/2 group"
            aria-label={z.city}
          >
            <span
              className={`block h-4 w-4 rounded-full ring-4 transition-all ${z.status === "Disponible" ? "bg-primary ring-primary/30" : "bg-white ring-white/30"
                } ${active === z.city ? "scale-150" : "group-hover:scale-125"}`}
            />
            <span
              className={`absolute left-1/2 -translate-x-1/2 -top-8 whitespace-nowrap rounded-full bg-black/80 text-white text-xs px-2 py-1 transition-opacity ${active === z.city ? "opacity-100" : "opacity-0"
                }`}
            >
              {z.city}
            </span>
          </motion.button>
        ))}
        <div className="absolute bottom-4 left-4 flex items-center gap-4 text-xs text-white bg-black/50 backdrop-blur rounded-full px-4 py-2">
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-primary" /> Disponible</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-white" /> Próximamente</span>
        </div>
      </div>
    </div>
  );
}
